import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import {GA_ID, PIXEL_ID} from 'shared/constants';
import { useTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import {
  retrieveToken,
  clearLoginError,
  toggleLivechat,
  fetchTwoFaEnabled,
  clearFetchTwoFaEnabled,
} from 'redux/users/actions';

import { Link } from 'react-router-dom';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Form } from 'semantic-ui-react';
import AuthCode from 'react-auth-code-input';
import ReactPixel from "react-facebook-pixel";

import CommonMessage from 'components/common/CommonMessage';

import InputTextField from 'components/redux-form-adapters/InputTextField';
import Button from 'components/elements/buttons/Button';
import GoogleButton from 'components/elements/buttons/AuthenticationButton/google';
import FacebookButton from "components/elements/buttons/AuthenticationButton/facebook";

import CommonLoader from 'components/common/CommonLoader';

import validate from './validate';

import './LoginForm.css';


const LoginForm = props => {
  const {
    loginError,
    handleSubmit,
    retrieveToken,
    clearLoginError,
    toggleLivechat,
    fetchTwoFaEnabled,
    clearFetchTwoFaEnabled,
    twoFaEnabled,
    valid,
    email,
    password,
  } = props;

  const { t } = useTranslation();
  const [showLoader, setShowLoader] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState(null);
  const twoFaMode = twoFaEnabled && twoFaEnabled.is_2fa_enabled;

  useEffect(() => {
    toggleLivechat(false);
    ReactGA.initialize(GA_ID);
    ReactGA.pageview(window.location.pathname + window.location.search);

    const pixelOptions = {
      autoConfig: true,
      debug: false,
    };
    ReactPixel.init(PIXEL_ID, null, pixelOptions);
    ReactPixel.pageView();

    return () => {
      setShowLoader(false);
      clearLoginError();
      toggleLivechat(true);
      clearFetchTwoFaEnabled();
    };
  }, []);

  useEffect(() => {
    if (twoFaMode === 'disabled') {
      onRetrieveToken();
    }
  }, [twoFaMode]);

  const _handleSubmit = () => {
    if (!twoFaMode) {
      fetchTwoFaEnabled(email.toLowerCase());
    } else {
      onRetrieveToken();
    }
  };

  const onRetrieveToken = () => {
    setShowLoader(true);
    if (twoFaMode === 'disabled') {
      retrieveToken(email.toLowerCase(), password);
    } else if (twoFaMode === 'enabled') {
      retrieveToken(email.toLowerCase(), password, twoFactorCode);
    }
  };

  const renderFields = () => (
    <>
      {/* Email Address */}
      <>
        <Field
          component={InputTextField}
          label={t("Email Address")}
          name="email"
          type="email"
          required
        />

        {/* Password */}
        <Field
          component={InputTextField}
          label={t("Enter your password")}
          name="password"
          type="password"
          required
        />
      </>

      {/* Two Factor Code */}
      {twoFaMode === 'enabled' &&
        <div className="two-factor-inputs-wrapper">
          <div className="inputs">
            <AuthCode onChange={value => setTwoFactorCode(value)} />
          </div>
          <div className="undertext">
            {t("Please enter the confirmation code you see on your authentication app")}
          </div>
        </div>
      }
    </>
  );

  return (
    <div className="login-form__component">
      <CommonLoader active={!!showLoader && !loginError} />

      <h1>{t("Log in to your")} <b>HeyLink.me</b> {t("account")}</h1>
      <Form
        onSubmit={handleSubmit(_handleSubmit)}
        error={!valid}
      >
        {renderFields()}

        <Link className="password-recovery-link" to="/password-recovery">{t("Forgot password?")}</Link>

        {(!twoFaMode || twoFaMode === 'disabled') && <Button submit>{t("Log In")}</Button>}
        {twoFaMode === 'enabled' &&
          <Button
            onClick={onRetrieveToken}
            disabled={!(twoFactorCode && twoFactorCode.length === 6)}
          >
            {t("Confirm")}
          </Button>
        }
        <span className="auth-label-text">{t("Or log in with your Google email")}</span>
        <GoogleButton verb={t("Log in")} />
        <FacebookButton verb={t("Log in")} />

        <div className="agrement-section">
          <span>{t("Don't have an account yet?")} </span>
          <Link to="/signup">{t("Sign Up")}</Link>
        </div>

      </Form>

      {loginError &&
        <CommonMessage
          error
          errors={loginError}
        />
      }

    </div>
  );
};

LoginForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  retrieveToken: PropTypes.func.isRequired,
  clearLoginError: PropTypes.func.isRequired,
  toggleLivechat: PropTypes.func.isRequired,
  fetchTwoFaEnabled: PropTypes.func.isRequired,
  clearFetchTwoFaEnabled: PropTypes.func.isRequired,
  loginError: PropTypes.object,
  valid: PropTypes.bool,
  twoFaEnabled: PropTypes.bool,
  email: PropTypes.string,
  password: PropTypes.string,
};

LoginForm.defaultProps = {
  loginError: null,
  valid: false,
  twoFaEnabled: null,
  email: false,
  password: null,
};

const mapStateToProps = (state) => {
  const selector = formValueSelector('LoginForm');
  return {
    email: selector(state, 'email'),
    password: selector(state, 'password'),
    accessToken: state.usersReducer.accessToken,
    loginError: state.usersReducer.loginError,
    twoFaEnabled: state.usersReducer.twoFaEnabled,
  };
};

export default connect(
  mapStateToProps,
  {
    retrieveToken,
    clearLoginError,
    toggleLivechat,
    fetchTwoFaEnabled,
    clearFetchTwoFaEnabled,
  },
)(reduxForm({
  form: 'LoginForm',
  validate,
})(LoginForm));
